import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useNetworkState } from 'react-use';
import { useInView } from 'react-intersection-observer';

import { PauseIcon } from 'icons';
import { PlayIcon } from 'icons';
import { Button } from '../Button';
import { cx } from 'utility';
import * as styles from './VideoAutoplay.module.scss';

const propTypes = {
    autoPlay: PropTypes.bool,
    className: PropTypes.string,
    loop: PropTypes.bool,
    videoUploadUrl: PropTypes.string,
};

const defaultProps = {
    autoPlay: true,
    className: '',
    loop: true,
    videoUploadUrl: '',
};

function getClasses({ className }) {
    const classes = cx(
        className,
        styles['video'],
    );

    return classes || undefined;
}

function VideoAutoplay({
    autoPlay,
    className,
    loop,
    videoUploadUrl,
}) {
    const [loadVideo, setLoadVideo] = useState(false);
    const [isVideoPlaying, setIsVideoPlaying] = useState(autoPlay);
    const [playPauseButtonText, setPlayPauseButtonText] = useState('Pause Video');
    const classes = getClasses({ className });
    const videoUploadRef = useRef(null);
    const [ref, inView, entry] = useInView({
        threshold: 0,
        triggerOnce: true,
    });

    // Check if the browser supports connection, if it does, don’t load the HTML5 video until network speed is checked, if it doesn’t load the HTML5 video regardless of network speed
    const supportsConnection = typeof window !== 'undefined' ? navigator.connection || navigator.mozConnection || navigator.webkitConnection : null;
    const networkState = supportsConnection ? useNetworkState() : '';
    if (!loadVideo) {
        if (networkState) {
            if (networkState.effectiveType === '3g' || networkState.effectiveType === '4g') {
                setLoadVideo(true);
            }
        } else {
            setLoadVideo(true);
        }
    }

    function toggleVideoPlaying() {
        setIsVideoPlaying(!isVideoPlaying);
        isVideoPlaying ? setPlayPauseButtonText('Play Video') : setPlayPauseButtonText('Pause Video');
        isVideoPlaying ? videoUploadRef.current.pause() : videoUploadRef.current.play();
    }

    function resetControls() {
        if (!loop) {
            setIsVideoPlaying(false);
            setPlayPauseButtonText('Play Video');
        }
    }

    return (
        <div className={classes} ref={ref}>
            {loadVideo && (
                <>
                    <Button className={styles['playPauseButton']} color="secondary" iconOnly={true} label={playPauseButtonText} onClick={toggleVideoPlaying} type="button">
                        {isVideoPlaying && (
                            <PauseIcon />
                        )}
                        {!isVideoPlaying && (
                            <PlayIcon />
                        )}
                    </Button>
                    {inView && (
                        <video autoPlay={isVideoPlaying} loop={loop} muted={true} playsInline={true} className={styles['videoElement']} onEnded={resetControls} loading="lazy" ref={videoUploadRef} src={videoUploadUrl} />
                    )}
                </>
            )}
        </div>
    );
}

VideoAutoplay.propTypes = propTypes;
VideoAutoplay.defaultProps = defaultProps;

export { VideoAutoplay };
